import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { conversationsEndpoints, messagesEndpoints as endpoints } from '@/api/endpoints';
import { ApiError, callDelete, callGet, callPost } from '@/api/fetcher';
import { invalidateQuery } from '@/hooks/queryCache/invalidateQuery';
import { formatScheduledAt } from '@/modules/humanChat/components/Conversation/MessageArea/ScheduledMessages/utils';
import { Conversation } from '@/shared/types/chat';
import { ScheduledMessage, ScheduledMessageCreate } from '@/shared/types/scheduledMessage';

// Stable empty reference so consumers don't see a new array identity each render.
const EMPTY_SCHEDULED_MESSAGES: ScheduledMessage[] = [];

const API = Object.freeze({
  listScheduledMessages: async (deskId: string, sessionId: string): Promise<ScheduledMessage[]> =>
    callGet(endpoints.scheduledMessages(deskId, sessionId)),

  createScheduledMessage: async (
    deskId: string,
    sessionId: string,
    data: ScheduledMessageCreate
  ): Promise<ScheduledMessage> => callPost(endpoints.scheduledMessages(deskId, sessionId), data),

  cancelScheduledMessage: async (
    deskId: string,
    sessionId: string,
    scheduledMessageId: string
  ): Promise<void> => callDelete(endpoints.scheduledMessage(deskId, sessionId, scheduledMessageId)),
});

/**
 * There are no socket events for scheduled messages — the list is refreshed on
 * conversation open, after own mutations, and on every `messages:created` for
 * the session (covers both a scheduled message firing and the server-side
 * auto-cancel on visitor reply).
 */
export const invalidateScheduledMessages = (
  queryClient: QueryClient,
  deskId: string,
  sessionId: string
) => {
  void queryClient.invalidateQueries({
    queryKey: [endpoints.scheduledMessages(deskId, sessionId)],
  });
};

// A fetch in flight may already hold the scheduled message that the new message sent or cancelled.
export const invalidateScheduledMessagesOnMessage = (
  queryClient: QueryClient,
  deskId: string,
  sessionId: string
) => {
  const query = queryClient
    .getQueryCache()
    .find<ScheduledMessage[]>({ queryKey: [endpoints.scheduledMessages(deskId, sessionId)] });
  if (query) {
    invalidateQuery(queryClient, query);
  }
};

export const useScheduledMessages = (deskId: string, sessionId?: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Read only the channel; same query key as `useConversations` so it dedupes
  // onto the view's fetch instead of mounting the full list hook.
  const { data: channel } = useQuery<Conversation, ApiError, Conversation['channel']>({
    queryKey: [conversationsEndpoints.conversation(deskId, sessionId ?? '')],
    queryFn: () => callGet(conversationsEndpoints.conversation(deskId, sessionId!)),
    enabled: Boolean(deskId && sessionId),
    select: (data) => data.channel,
    staleTime: Infinity,
  });

  // Scheduling is WhatsApp-templates-only; every surface hides on other channels.
  const isEnabled = channel === 'whatsapp';

  // Only build the endpoint-based query key once a session exists so we never
  // register a `/conversations/undefined/...` cache entry.
  const listEndpoint = sessionId ? endpoints.scheduledMessages(deskId, sessionId) : null;

  const { data: scheduledMessages, status: listStatus } = useQuery<ScheduledMessage[], ApiError>({
    queryKey: [listEndpoint],
    // `enabled` guards execution, so `sessionId` is always defined here.
    queryFn: () => API.listScheduledMessages(deskId, sessionId!),
    enabled: Boolean(deskId && sessionId && isEnabled),
    // Invalidation-driven freshness; small staleTime trims focus/remount refetches.
    staleTime: 30_000,
  });

  const { mutate: createScheduledMessage, status: createStatus } = useMutation<
    ScheduledMessage,
    ApiError,
    ScheduledMessageCreate
  >({
    mutationFn: (data) => {
      if (!sessionId) {
        return Promise.reject(new Error('Cannot schedule a message without a session'));
      }
      return API.createScheduledMessage(deskId, sessionId, data);
    },
    // Mutation-scope (useMutation) callbacks still run when the scheduling
    // component unmounts mid-POST — mutate-scope ones are skipped, which would
    // lose the toast if the agent switched conversations or closed the modal
    // in flight.
    onSuccess: (_data, variables) => {
      toast.success(t('scheduled_messages.message_scheduled'), {
        description: t('scheduled_messages.scheduled_for', {
          0: formatScheduledAt(variables.scheduled_at),
        }),
      });
      if (sessionId) {
        invalidateScheduledMessages(queryClient, deskId, sessionId);
      }
    },
    onError: (error) => {
      if (error.code === 'scheduled-message-limit-reached') {
        toast.error(t('scheduled_messages.limit_reached'));
        return;
      }
      if (error.code === 'scheduled-message-conversation-not-open') {
        toast.error(t('scheduled_messages.conversation_not_open'));
        // The conversation is stale (closed/trashed elsewhere) — refresh it.
        if (sessionId) {
          void queryClient.invalidateQueries({
            queryKey: [conversationsEndpoints.conversation(deskId, sessionId)],
          });
        }
        return;
      }
      // Covers the stricter template validation 400s — surface the server message.
      toast.error(error.message);
    },
  });

  const { mutate: cancelScheduledMessage, status: cancelStatus } = useMutation<
    void,
    ApiError,
    string
  >({
    mutationFn: (scheduledMessageId) => {
      if (!sessionId) {
        return Promise.reject(new Error('Cannot cancel a scheduled message without a session'));
      }
      return API.cancelScheduledMessage(deskId, sessionId, scheduledMessageId);
    },
    onSuccess: () => {
      if (sessionId) {
        invalidateScheduledMessages(queryClient, deskId, sessionId);
      }
    },
    onError: (error) => {
      if (error.code === 'scheduled-message-in-flight') {
        toast.error(t('scheduled_messages.in_flight'));
        // The job is already firing — the pending list is stale, refresh it.
        if (sessionId) {
          invalidateScheduledMessages(queryClient, deskId, sessionId);
        }
        return;
      }
      toast.error(error.message);
    },
  });

  return {
    scheduledMessages: scheduledMessages ?? EMPTY_SCHEDULED_MESSAGES,
    pendingCount: scheduledMessages?.length ?? 0,
    listStatus,
    createScheduledMessage,
    createStatus,
    cancelScheduledMessage,
    cancelStatus,
    isEnabled,
  };
};
